import { useLingui } from '@lingui/react/macro';
import { Zap } from 'lucide-react';
import { Image } from '@/routes/-components/image';
import { DiamondGrid, Squiggle, diamondGlows } from '../primitives';

/**
 * The dot that closes the vertical rule between the two statements: an 8px olive disc with a 4px hole
 * punched in it, which the hand-off draws as two stacked circles rather than a ring.
 */
function RuleDot() {
	return (
		<span aria-hidden className="bg-olive relative size-2 shrink-0 rounded-full">
			<span className="bg-ink absolute top-1/2 left-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full" />
		</span>
	);
}

/**
 * "Worldcoo nació en Barcelona en 2012…" — the story that opens About.
 *
 * One ink band holding two centred statements with the photo collage between them. The hand-off splits
 * it into two frames of 643 and 919, but they share a background and read as one stretch, so it is a
 * single section here.
 *
 * The photos deliberately BLEED off both edges — the export puts the left one at x=-185 of a 1440 canvas
 * — which is why they are positioned rather than laid out in the grid. Below `xl` they come inside and
 * stack under the card, since a photo cropped to a sliver on a phone reads as a mistake.
 */
export function Story() {
	const { t } = useLingui();
	return (
		<section className="bg-ink relative overflow-hidden text-white">
			{/* The same dotted texture the rest of the site uses, in the hand-off's two glows.
			    Figma gives each glow as a 777x501 ellipse by its TOP-LEFT corner, so the mask centre is that
			    corner plus (388.5, 250.5) — 250px lower than the `top` it prints. Taking `top` as the centre
			    put both glows above their real positions and left most of this band bare.
			    The second glow is expressed as a percentage because it belongs to the hand-off's second frame:
			    the copy here is shorter than the Spanish, so the band renders shorter than the 1562px drawn,
			    and a fixed offset would drift. */}
			<DiamondGrid
				color="rgba(255,255,255,0.35)"
				opacity={1}
				masks={[diamondGlows({ x: '50%', y: '-38px' }, { x: '50%', y: '72%' })]}
			/>

			<div className="relative mx-auto max-w-[1440px] px-6 py-20 md:py-28 xl:px-10">
				<p className="mx-auto max-w-[1022px] text-center text-2xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
					{t`Worldcoo was born in Barcelona in 2012 with a very clear purpose: to help NGOs find the funds they need to drive their projects forward.`}
				</p>

				{/* 200px rule plus the dot, straight off the export. Hidden on small screens, where it would
				    push the collage below the fold for no gain. */}
				<div aria-hidden className="hidden flex-col items-center pt-16 md:flex">
					<span className="h-[200px] w-px bg-white/25" />
					<RuleDot />
				</div>

				<div className="relative mt-12 flex flex-col items-center gap-8 md:mt-20 xl:mt-32 xl:block xl:h-[489px]">
					{/* 578x393 at x=-185: it hangs off the left edge on purpose. */}
					<div className="w-full max-w-[578px] overflow-hidden rounded-3xl xl:absolute xl:top-[153px] xl:-left-[185px] xl:w-[578px]">
						<Image
							src="/redesign/about/boy-playground.jpg"
							alt={t`A boy playing in a playground`}
							height={786}
							aspectRatio={578 / 393}
							objectFit="cover"
							className="h-full w-full object-cover!"
						/>
					</div>

					{/* 558x364, offset 16px left of centre, and the only apricot surface on this dark band. */}
					<div className="bg-apricot text-ink relative z-10 flex w-full max-w-[558px] flex-col gap-6 rounded-3xl p-8 xl:absolute xl:top-0 xl:left-1/2 xl:-ml-4 xl:min-h-[364px] xl:w-[558px] xl:-translate-x-1/2">
						<Zap className="text-brand size-8 shrink-0 fill-current" strokeWidth={0} />
						<p className="max-w-[494px] text-xl leading-[1.3] tracking-[-0.02em] md:text-2xl xl:text-[2rem]">
							{t`Since then, we have been building innovative fundraising tools so that companies, their employees and their customers can add a small microdonation to their everyday transactions.`}
						</p>
					</div>

					{/* 385x393 at x=1015, so it runs off the right edge just as the other runs off the left. */}
					<div className="w-full max-w-[385px] overflow-hidden rounded-3xl xl:absolute xl:top-[64px] xl:left-[1015px] xl:w-[385px]">
						<Image
							src="/redesign/about/girl-laughing.jpg"
							alt={t`A girl laughing outdoors`}
							height={786}
							aspectRatio={385 / 393}
							objectFit="cover"
							className="h-full w-full object-cover!"
						/>
					</div>
				</div>

				<p className="mx-auto mt-16 max-w-[943px] text-center text-xl leading-[1.3] font-medium md:mt-24 md:text-[2rem]">
					{t`Our premise is clear: to connect companies with `}
					<Squiggle lineClassName="text-olive">{t`social causes`}</Squiggle>
					{t` efficiently, securely and transparently.`}
				</p>
			</div>
		</section>
	);
}
